import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BuildingFilterDto,
  BuildingItemDto,
  BuildingService,
  RegionItemDto,
  TeamItemDto,
} from '@artemis-software/wr-api';
import { FormBuilder, FormGroup } from '@angular/forms';
import { currentTenantId } from '@/utils/admin-utils';
import { parseBooleanParam } from '@/utils/url-utils';

const MY_TEAM_ONLY_PARAM = 'myTeamOnly';

function myTeamOnlyOverride(param: string | null): Partial<BuildingFilterDto> | undefined {
  const myTeamOnly = parseBooleanParam(param);
  if (myTeamOnly === undefined) {
    return undefined;
  }
  return { myTeamOnly };
}


@Component({
  standalone: false,
  selector: 'wr-external-building-overview-page',
  templateUrl: './external-building-overview-page.component.html',
  styleUrls: ['./external-building-overview-page.component.scss'],
})
export class ExternalBuildingOverviewPageComponent {
  private readonly buildingService = inject(BuildingService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);

  tenantId = currentTenantId();
  form: FormGroup;
  readonly filterOverride = myTeamOnlyOverride(this.route.snapshot.queryParamMap.get(MY_TEAM_ONLY_PARAM));

  readonly loadBuildings = (filter: BuildingFilterDto) => this.buildingService.findAll({ ...filter, accessibleForTenantId: this.tenantId() });
  readonly getCount = (filter: BuildingFilterDto) => this.buildingService.getCount({ ...filter, accessibleForTenantId: this.tenantId() });

  constructor() {
    this.form = this.formBuilder.group({
      fullTextSearch: this.formBuilder.control(''),
      buildingClass: this.formBuilder.control<string | null>(null),
      buildingType: this.formBuilder.control<string | null>(null),
      region: this.formBuilder.control<RegionItemDto | null>(null),
      regionId: this.formBuilder.control<string | null>(null),
      team: this.formBuilder.control<TeamItemDto | null>(null),
      teamId: this.formBuilder.control<string | null>(null),
      myTeamOnly: this.formBuilder.control<boolean>(false),
    });

    this.form.get('region')?.valueChanges.subscribe((region) => {
      if (region) {
        this.form.get('regionId')?.setValue(region.id);
      } else {
        this.form.get('regionId')?.setValue(null);
      }
    });

    this.form.get('team')?.valueChanges.subscribe((team) => {
      if (team) {
        this.form.get('teamId')?.setValue(team.id);
      } else {
        this.form.get('teamId')?.setValue(null);
      }
    });
  }

  writeMyTeamOnlyToUrl(filter: BuildingFilterDto): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [MY_TEAM_ONLY_PARAM]: filter.myTeamOnly === true },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  navigateToBuilding(event: BuildingItemDto): void {
    this.router.navigate(['building', event.id]);
  }

  navigateToEquipment(event: Event, id: string): void {
    event.stopPropagation();
    this.router.navigate(['external', 'buildingEquipments'], { queryParams: { buildingId: id } });
  }

  protected readonly signal = signal;
}
